import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import styles from "./css/HelpPostMap.css";

/* global TMap */

// Helper function to convert post type values to user-friendly labels
const getPostTypeLabel = (type) => {
  const postTypeLabels = {
    'lost': '丢失',
    'found': '发现',
    'adoption': '领养',
    'rescue': '救助',
    'other': '其它'
  };
  
  return postTypeLabels[type] || type;
};

const HelpPostMap = forwardRef(({ helpPosts, hoveredPostId, showInfoWindow = true }, ref) => {
  const CreatePostMap = useRef(null);
  const mapInstance = useRef(null);
  
  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    centerMap: (latitude, longitude) => {
      if (!mapInstance.current) return;
      
      try {
        const lat = parseFloat(latitude);
        const lng = parseFloat(longitude);
        
        if (!isNaN(lat) && !isNaN(lng)) {
          const location = new TMap.LatLng(lat, lng);
          mapInstance.current.setCenter(location);
          mapInstance.current.setZoom(15); // Set an appropriate zoom level
          console.log(`HelpPostMap - Centered map on: Lat: ${lat}, Lng: ${lng}`);
        } else {
          console.error("HelpPostMap - Invalid coordinates:", latitude, longitude);
        }
      } catch (error) {
        console.error("HelpPostMap - Error centering map:", error);
      }
    }
  }));

  // Define color scheme for different post types (using backend values)
  const postTypeColors = {
    'lost': '#FF5733',      // Orange
    'found': '#33FF57',     // Green
    'adoption': '#5733FF',  // Purple
    'rescue': '#33A1FF',    // Blue
    'other': '#FFC300'      // Yellow
  };

  // Store the previous center and zoom to restore when hover ends
  const [previousMapState, setPreviousMapState] = useState(null);

  // Effect to handle hovering on a post
  useEffect(() => {
    if (!mapInstance.current || !hoveredPostId) {
      // If no map instance or no post is hovered, do nothing
      return;
    }

    // Find the hovered post
    const hoveredPost = helpPosts.find(post => post.id === hoveredPostId);
    
    if (!hoveredPost || !hoveredPost.latitude || !hoveredPost.longitude) {
      // If post doesn't have coordinates, do nothing
      return;
    }

    try {
      // Save current map state if not already saved
      if (!previousMapState) {
        setPreviousMapState({
          center: mapInstance.current.getCenter(),
          zoom: mapInstance.current.getZoom()
        });
      }

      // Parse coordinates
      const lat = parseFloat(hoveredPost.latitude);
      const lng = parseFloat(hoveredPost.longitude);
      
      if (!isNaN(lat) && !isNaN(lng)) {
        // Center map on hovered post with zoom level 18
        const postLocation = new TMap.LatLng(lat, lng);
        mapInstance.current.setCenter(postLocation);
        mapInstance.current.setZoom(18);
      }
    } catch (error) {
      console.error("HelpPostMap - Error centering on hovered post:", error);
    }

    // No cleanup function - we want to keep the map centered on the last hovered post
    // so users can interact with that area of the map
  }, [hoveredPostId, helpPosts, previousMapState]);

  useEffect(() => {
    // Check if TMap is already loaded
    const initializeMap = () => {
      if (window.TMap) {
        // TMap is already loaded, initialize map directly
        initMap();
      } else {
        // Load TMap script dynamically only if it's not already loaded
        const existingScript = document.getElementById('TmapsScript');
        if (!existingScript) {
          // Define the callback function globally
          window.initTmap = function() {
            initMap();
          };
          
          const script = document.createElement("script");
          script.id = 'TmapsScript';
          script.charset = "utf-8";
          script.src = `https://map.qq.com/api/gljs?v=1.exp&key=GDTBZ-QD4WC-G5722-A3V7A-6BPM3-35B7P&callback=initTmap`;
          script.onerror = (error) => {
            console.error("HelpPostMap - TMap script loading error:", error);
          };
          
          document.head.appendChild(script);
        } else {
          // Script exists but TMap isn't loaded yet, set up a new callback
          window.initTmap = function() {
            initMap();
          };
        }
      }
    };

    const initMap = () => {
      try {
        if (!window.TMap) {
          console.error("HelpPostMap - TMap API not available");
          return;
        }
        
        if (!CreatePostMap.current) {
          console.error("HelpPostMap - Map container not found");
          return;
        }
        
        // Default center coordinates (Guangzhou)
        const defaultCenter = new TMap.LatLng(23.129110, 113.264385);
        
        // Create map instance with proper settings
        mapInstance.current = new TMap.Map(CreatePostMap.current, {
          center: defaultCenter,
          zoom: 17,  // Set zoom level as in the example
        });
        
        // For HelpPostDetail, we want to prioritize centering on the post location
        // We'll check if there's only one post with coordinates first
        if (helpPosts.length === 1 && helpPosts[0].latitude && helpPosts[0].longitude) {
          try {
            const lat = parseFloat(helpPosts[0].latitude);
            const lng = parseFloat(helpPosts[0].longitude);
            
            if (!isNaN(lat) && !isNaN(lng)) {
              console.log(`HelpPostMap - Centering on post location: Latitude: ${lat}, Longitude: ${lng}`);
              const postLocation = new TMap.LatLng(lat, lng);
              mapInstance.current.setCenter(postLocation);
              // Set zoom level to 15 for single post view
              mapInstance.current.setZoom(15);
            } else {
              // Fallback to default location if coordinates are invalid
              console.error("HelpPostMap - Invalid post coordinates:", helpPosts[0].latitude, helpPosts[0].longitude);
              mapInstance.current.setCenter(defaultCenter);
            }
          } catch (error) {
            console.error("HelpPostMap - Error setting map center to post location:", error);
            mapInstance.current.setCenter(defaultCenter);
          }
        } 
        // If not a single post view, try to get user's location
        else if ("geolocation" in navigator) {
          console.log("HelpPostMap - Requesting user location");
          navigator.geolocation.getCurrentPosition(
            // Success callback - user allowed location access
            (position) => {
              const { latitude, longitude } = position.coords;
              console.log(`HelpPostMap - User's current position: Latitude: ${latitude}, Longitude: ${longitude}`);
              
              try {
                // Ensure coordinates are properly formatted as numbers
                const lat = parseFloat(latitude);
                const lng = parseFloat(longitude);
                
                if (!isNaN(lat) && !isNaN(lng)) {
                  // Create a proper LatLng object with numeric values
                  const userLocation = new TMap.LatLng(lat, lng);
                  mapInstance.current.setCenter(userLocation);
                } else {
                  // Fallback to Guangzhou if coordinates are invalid
                  console.log("HelpPostMap - Invalid coordinates, using default location");
                  mapInstance.current.setCenter(defaultCenter);
                }
              } catch (error) {
                // Gracefully handle errors when setting map center
                console.log("HelpPostMap - Could not set user location, using default location");
                // Fallback to Guangzhou if there's an error
                mapInstance.current.setCenter(defaultCenter);
              }
            },
            // Error callback - user denied location or other error
            (error) => {
              // Gracefully handle geolocation errors with appropriate messages based on error code
              let errorMessage = "Location access unavailable";
              
              switch(error.code) {
                case error.PERMISSION_DENIED:
                  errorMessage = "Location access denied by user";
                  break;
                case error.POSITION_UNAVAILABLE:
                  errorMessage = "Location information unavailable";
                  break;
                case error.TIMEOUT:
                  errorMessage = "Location request timed out";
                  break;
                case error.UNKNOWN_ERROR:
                default:
                  errorMessage = "Unknown location error";
                  break;
              }
              
              // Log as info instead of error since this is an expected scenario
              console.log(`HelpPostMap - ${errorMessage}, using default location`);
              
              // Fallback to Guangzhou
              mapInstance.current.setCenter(defaultCenter);
            },
            // Options for geolocation request
            {
              timeout: 10000,        // 10 second timeout
              maximumAge: 60000,     // Accept cached positions up to 1 minute old
              enableHighAccuracy: false  // Don't require high accuracy to reduce errors
            }
          );
        } else {
          // Geolocation not supported, center on Guangzhou
          console.log("HelpPostMap - Geolocation is not supported by this browser");
          mapInstance.current.setCenter(defaultCenter);
          console.log("HelpPostMap - Fallback: Map centered on Guangzhou");
        }

        // Prepare marker geometries for each help post
        const geometries = [];
        
        console.log("HelpPostMap - Creating markers for", helpPosts.length, "posts");
        
        helpPosts.forEach((post, index) => {
          if (post.latitude && post.longitude) {
            try {
              // Ensure latitude and longitude are numbers
              const lat = parseFloat(post.latitude);
              const lng = parseFloat(post.longitude);
              
              if (isNaN(lat) || isNaN(lng)) {
                console.error(`HelpPostMap - Invalid coordinates for post ${index}:`, post.latitude, post.longitude);
                return;
              }
              
              // Log the post object to see all available properties
              console.log(`HelpPostMap - Post ${index} object:`, post);
              
              // Add geometry for this post
              geometries.push({
                id: `post-${post.id}`,
                // styleId will be updated later based on post type
                position: new TMap.LatLng(lat, lng),
                properties: {
                  title: post.title,
                  type: post.type,
                  post_type: post.post_type,
                  description: post.description,
                  id: post.id,
                  // Copy all properties from the post object to ensure we have everything
                  ...post
                }
              });
              
            } catch (error) {
              console.error(`HelpPostMap - Error creating marker geometry ${index}:`, error);
            }
          } else {
            console.warn(`HelpPostMap - Post ${index} missing latitude or longitude`);
          }
        });

        // Common marker style settings
        const markerSize = 40;
        const markerAnchor = { x: 17, y: 50 };
        
        // Define marker styles for different post types based on backend values
        const markerStyles = {
          "lost": new TMap.MarkerStyle({
            "width": markerSize,
            "height": markerSize,
            "anchor": markerAnchor,
            "src": '/markers/marker-lost.png'
          }),
          "found": new TMap.MarkerStyle({
            "width": markerSize,
            "height": markerSize,
            "anchor": markerAnchor,
            "src": '/markers/marker-found.png'
          }),
          "adoption": new TMap.MarkerStyle({
            "width": markerSize,
            "height": markerSize,
            "anchor": markerAnchor,
            "src": '/markers/marker-adoption.png'
          }),
          "rescue": new TMap.MarkerStyle({
            "width": markerSize,
            "height": markerSize,
            "anchor": markerAnchor,
            "src": '/markers/marker-rescue.png'
          }),
          "other": new TMap.MarkerStyle({
            "width": markerSize,
            "height": markerSize,
            "anchor": markerAnchor,
            "src": '/markers/marker-others.png'
          })
        };

        // Default marker style for fallback
        const defaultMarkerStyle = new TMap.MarkerStyle({
          "width": markerSize,
          "height": markerSize,
          "anchor": markerAnchor,
          "src": '/markers/marker-others.png'
        });

        // Prepare styles object for MultiMarker
        const styles = {
          "default": defaultMarkerStyle
        };
        
        // Add each post type style to the styles object
        Object.keys(markerStyles).forEach(type => {
          styles[type] = markerStyles[type];
        });

        // Update geometries to use the appropriate style based on post type
        geometries.forEach(geometry => {
          // Set the styleId based on post type, or use default if type not found
          const postType = geometry.properties.post_type || geometry.properties.type;
          
          // Log the post type and all properties for debugging
          console.log(`HelpPostMap - Post ${geometry.id} properties:`, geometry.properties);
          console.log(`HelpPostMap - Post ${geometry.id} has type: "${postType}"`);
          
          // Check if we have a style for this post type
          if (postType && styles[postType]) {
            geometry.styleId = postType;
            console.log(`HelpPostMap - Using marker style: ${postType}`);
          } else {
            // If post type is not recognized, try to determine the appropriate type
            let determinedType = "other";
            
            // Check if the title or description contains keywords that might indicate the post type
            const title = (geometry.properties.title || "").toLowerCase();
            const description = (geometry.properties.description || "").toLowerCase();
            const combinedText = title + " " + description;
            
            if (combinedText.includes("lost") || combinedText.includes("missing") || 
                combinedText.includes("丢失") || combinedText.includes("丢了")) {
              determinedType = "lost";
            } else if (combinedText.includes("found") || combinedText.includes("发现") || 
                       combinedText.includes("找到")) {
              determinedType = "found";
            } else if (combinedText.includes("adopt") || combinedText.includes("领养")) {
              determinedType = "adoption";
            } else if (combinedText.includes("rescue") || combinedText.includes("救助")) {
              determinedType = "rescue";
            }
            
            geometry.styleId = determinedType;
            console.log(`HelpPostMap - Post type not recognized. Determined type: ${determinedType}`);
          }
        });

        // Create MultiMarker with all post markers
        const marker = new TMap.MultiMarker({
          id: 'marker-layer',
          map: mapInstance.current,
          styles: styles,
          geometries: geometries
        });
        
        // Store infowindows in a map to reference them by ID
        const infoWindows = {};
        
        // Define a function to close a specific infowindow by ID
        window.closeInfoWindowById = function(infoWindowId) {
            if (infoWindows[infoWindowId]) {
                infoWindows[infoWindowId].close();
                delete infoWindows[infoWindowId];
            }
        };
        
        // Create and display InfoWindows for all markers if showInfoWindow is true
        if (showInfoWindow) {
          geometries.forEach((geometry) => {
              const post = geometry.properties;
              const infoWindowId = `infoWindow-${post.id}`;
              
              // Create a new custom info window for each marker
              const infoWindow = new TMap.InfoWindow({
                  id: infoWindowId,
                  map: mapInstance.current,
                  position: geometry.position,
                  enableCustom: true,
                  content: `
                    <div class="infoWindow" style="width: 200px; background: #fff; border-radius: 6px; padding: 10px; box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15); position: relative; cursor: pointer;" onclick="window.location.href='/help-post/${post.id}'">
                      <h3 style="margin: 0 0 6px 0; font-size: 13px; font-weight: bold; color:rgb(173, 114, 72); padding-right: 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                        ${post.post_type || post.type ? `[${getPostTypeLabel(post.post_type || post.type)}] ` : ''}${post.title}
                      </h3>
                      <p style="margin: 4px 0; font-size: 11px; color: #666; overflow: hidden;">
                        ${post.description ? `<span style="display: inline-block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 128px;">${post.description.substring(0, 24)}${post.description.length > 24 ? '...' : ''}</span>` : ''}
                      </p>
                      <span style="position: absolute; bottom: -6px; left: 50%; margin-left: -6px; width: 0; height: 0; border-left: 6px solid transparent; border-right: 6px solid transparent; border-top: 6px solid #fff;"></span>
                    </div>
                  `,
                  offset: { x: 0, y: -55 }
              });
              
              // Store the infoWindow in our map
              infoWindows[infoWindowId] = infoWindow;
              
              // Open the info window
              infoWindow.open();
          });
        }
        
        // Add click event listener to markers to navigate to post page
        marker.on("click", function(evt) {
            const post = evt.geometry.properties;
            // Navigate to the post page when marker is clicked
            window.location.href = `/help-post/${post.id}`;
        });

        console.log("HelpPostMap - Created", geometries.length, "markers successfully");

        // Fit the map to show all markers if we have multiple posts
        if (geometries.length > 1) {
          try {
            console.log("HelpPostMap - Fitting map to show all markers");
            
            // Create a bounds object
            const bounds = new TMap.LatLngBounds();
            
            // Extend bounds with all marker positions
            geometries.forEach((geometry) => {
              bounds.extend(geometry.position);
            });
            
            // Fit the map to the bounds
            mapInstance.current.fitBounds(bounds);
            
            console.log("HelpPostMap - Map fitted to show all markers");
          } catch (error) {
            console.error("HelpPostMap - Error setting map view:", error);
          }
        }
      } catch (error) {
        console.error("HelpPostMap - Map initialization error:", error);
      }
    };

    // Always initialize the map, even if there are no help posts
    console.log("HelpPostMap - Starting map initialization with", helpPosts.length, "posts");
    initializeMap();

    return () => {
      // Clean up
      console.log("HelpPostMap - Cleaning up");
      if (window.initTmap) {
        console.log("HelpPostMap - Removing initTmap from window");
        delete window.initTmap;
      }
      
      // Properly clean up the map instance
      if (mapInstance.current) {
        console.log("HelpPostMap - Removing map instance reference");
        // Don't try to remove all listeners at once as it's not supported
        // Just set to null to allow garbage collection
        mapInstance.current = null;
      }
      
      // Clear the map container
      if (CreatePostMap.current) {
        while (CreatePostMap.current.firstChild) {
          CreatePostMap.current.removeChild(CreatePostMap.current.firstChild);
        }
      }
    };
  }, [helpPosts]);

  return (
    <div
      ref={CreatePostMap}
      className={styles.container}
      style={{ height: "100%", width: "100%" }}
    />
  );
});

export default HelpPostMap;
